import { useState } from "react";
import { IconButton, Tooltip } from "@mui/material";
import { AttachFile } from "@mui/icons-material";
import {
  downloadInvoiceAttachment,
  downloadLocalAttachmentFile,
  getInvoiceAttachment,
  readInvoiceAttachmentMemory,
} from "../utils/invoiceAttachmentUtils";
import { getOrganizationId } from "../utils/organizationUtils";

/**
 * Table / list download control for invoice attachments.
 * Accepts an invoice row object, a raw URL/path string, or { attachment }.
 */
const InvoiceAttachmentDownloadButton = ({
  attachment,
  invoice = null,
  attachmentFile = null,
  disabled = false,
  size = "small",
  emptyLabel = "N/A",
  silent = false,
}) => {
  const [loading, setLoading] = useState(false);

  const attachmentValue =
    getInvoiceAttachment(invoice) ||
    getInvoiceAttachment({ attachment }) ||
    resolveRawAttachment(attachment) ||
    (invoice?.id != null
      ? readInvoiceAttachmentMemory(getOrganizationId(null))[
          String(invoice.id)
        ] || ""
      : "");

  const canDownload =
    Boolean(attachmentValue) || attachmentFile instanceof File;

  if (!canDownload) {
    return emptyLabel;
  }

  const handleDownload = async () => {
    if (loading || disabled) return;
    setLoading(true);
    try {
      if (attachmentFile instanceof File) {
        await downloadLocalAttachmentFile(attachmentFile, { silent });
      } else if (attachmentValue) {
        await downloadInvoiceAttachment(attachmentValue, { silent });
      }
    } catch {
      // downloadInvoiceAttachment already toasts errors
    } finally {
      setLoading(false);
    }
  };

  return (
    <Tooltip title="Download attachment">
      <span>
        <IconButton
          color="primary"
          size={size}
          onClick={handleDownload}
          disabled={disabled || loading}
          aria-label="Download attachment"
        >
          <AttachFile fontSize={size} />
        </IconButton>
      </span>
    </Tooltip>
  );
};

function resolveRawAttachment(value) {
  if (value == null || value === "") return "";
  if (typeof value === "string") return value.trim();
  if (typeof value === "object" && value.attachment != null) {
    return getInvoiceAttachment(value);
  }
  return "";
}

export default InvoiceAttachmentDownloadButton;
